import { useEffect, useMemo, useState, type ReactNode } from "react";
import { AlertTriangle, XCircle } from "lucide-react";
import {
  useGetPaymentControlPlaneReadinessQuery,
  useUpdatePaymentControlPlaneMutation,
  type PaymentControlPlaneReadinessResponse,
} from "../../../state/domainRouterApi";
import { SupportErrorMessage } from "../../../components/overlays/EngineeringSupportBanner";
import { Button, Card, Input, SectionHeader } from "./ui";

const readText = (value: unknown, fallback = ""): string => {
  const next = String(value ?? "").trim();
  return next || fallback;
};

type PaymentIssueSeverity =
  PaymentControlPlaneReadinessResponse["issues"][number]["severity"];
type SchoolPaymentMethodConfigurationCatalogEntry =
  PaymentControlPlaneReadinessResponse["paymentMethodConfigurations"]["schoolCatalog"][number];
type PaymentMethodConfigurationResolvedAssignment =
  PaymentControlPlaneReadinessResponse["paymentMethodConfigurations"]["effectiveAssignments"][number];
type PaymentCheckoutOperationKey =
  PaymentMethodConfigurationResolvedAssignment["operationKey"];

type PaymentMethodConfigurationOperationDefinition = {
  operationKey: PaymentCheckoutOperationKey;
  category: string;
  label: string;
  description: string;
};

type PaymentMethodConfigurationDraftState = {
  id: string;
  label: string;
  paymentMethodConfigurationId: string;
  enabled: boolean;
};

type EditablePaymentMethodConfigurationAssignment = {
  operationKey: PaymentCheckoutOperationKey;
  catalogEntryId: string | null;
  resolvedCatalogEntry: PaymentMethodConfigurationResolvedAssignment["resolvedCatalogEntry"];
};

const PAYMENT_METHOD_CONFIGURATION_OPERATIONS: PaymentMethodConfigurationOperationDefinition[] =
  [
    {
      operationKey: "tuition_checkout",
      category: "Tuition",
      label: "Checkout",
      description:
        "Parent tuition hosted checkout for installments, overdue balances, custom amounts, and pay-in-full.",
    },
    {
      operationKey: "enrollment_registration_checkout",
      category: "Enrollment",
      label: "Registration checkout",
      description:
        "Parent enrollment registration fee checkout from the admissions enrollment checklist.",
    },
    {
      operationKey: "donations_checkout_one_time",
      category: "Donations",
      label: "One-time checkout",
      description: "Standard one-time parent donations checkout.",
    },
    {
      operationKey: "donations_checkout_recurring",
      category: "Donations",
      label: "Recurring checkout",
      description: "Monthly parent donations checkout.",
    },
    {
      operationKey: "donations_parnes_hayom_checkout",
      category: "Donations",
      label: "Parnes HaYom checkout",
      description: "Day-sponsorship checkout under Parnes HaYom.",
    },
    {
      operationKey: "tip_a_teacher_checkout",
      category: "Tip-a-Teacher",
      label: "Checkout",
      description: "Parent appreciation tips for teachers.",
    },
    {
      operationKey: "camps_registration_checkout",
      category: "Camps",
      label: "Registration checkout",
      description:
        "Initial camp registration checkout from the registration flow.",
    },
    {
      operationKey: "camps_balance_checkout",
      category: "Camps",
      label: "Balance checkout",
      description:
        "Outstanding camp balance checkout from existing registrations.",
    },
  ];

type PaymentMethodConfigurationLaneGroup = {
  id: string;
  label: string;
  match: (operationKey: PaymentCheckoutOperationKey) => boolean;
};

const PAYMENT_METHOD_CONFIGURATION_LANE_GROUPS: PaymentMethodConfigurationLaneGroup[] =
  [
    {
      id: "enrollment",
      label: "Enrollment",
      match: (operationKey) => operationKey.startsWith("enrollment_"),
    },
    {
      id: "tuition",
      label: "Tuition",
      match: (operationKey) => operationKey.startsWith("tuition_"),
    },
    {
      id: "donations_and_sponsorships",
      label: "Donations and sponsorships",
      match: (operationKey) => operationKey.startsWith("donations_"),
    },
    {
      id: "camps",
      label: "Camps",
      match: (operationKey) => operationKey.startsWith("camps_"),
    },
    {
      id: "tip_a_teacher",
      label: "Tip a teacher",
      match: (operationKey) => operationKey.startsWith("tip_a_teacher_"),
    },
    {
      id: "other",
      label: "Other",
      match: () => false,
    },
  ];

const emptySchoolPaymentMethodConfigurationDraft =
  (): PaymentMethodConfigurationDraftState => ({
    id: "",
    label: "",
    paymentMethodConfigurationId: "",
    enabled: true,
  });

const slugifyPaymentMethodConfigurationId = (value: string): string =>
  readText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "school_pmc";

const normalizeSchoolPaymentMethodConfigurationCatalog = (
  entries: SchoolPaymentMethodConfigurationCatalogEntry[],
): SchoolPaymentMethodConfigurationCatalogEntry[] =>
  entries
    .map((entry) => ({
      id: readText(entry.id),
      label: readText(entry.label),
      paymentMethodConfigurationId: readText(
        entry.paymentMethodConfigurationId,
      ),
      enabled: Boolean(entry.enabled),
      source: "school" as const,
    }))
    .sort(
      (left, right) =>
        left.label.localeCompare(right.label) ||
        left.id.localeCompare(right.id),
    );

const normalizePaymentMethodConfigurationAssignments = (
  entries: EditablePaymentMethodConfigurationAssignment[],
): Array<{
  operationKey: PaymentCheckoutOperationKey;
  catalogEntryId: string | null;
}> =>
  entries
    .map((entry) => ({
      operationKey: entry.operationKey,
      catalogEntryId: readText(entry.catalogEntryId) || null,
    }))
    .sort((left, right) => left.operationKey.localeCompare(right.operationKey));

const serializePaymentMethodConfigurationState = (
  schoolCatalog: SchoolPaymentMethodConfigurationCatalogEntry[],
  assignments: EditablePaymentMethodConfigurationAssignment[],
): string =>
  JSON.stringify({
    schoolCatalog:
      normalizeSchoolPaymentMethodConfigurationCatalog(schoolCatalog),
    assignments: normalizePaymentMethodConfigurationAssignments(assignments),
  });

const buildEditablePaymentMethodConfigurationAssignments = (
  entries: PaymentControlPlaneReadinessResponse["paymentMethodConfigurations"]["effectiveAssignments"],
): EditablePaymentMethodConfigurationAssignment[] =>
  PAYMENT_METHOD_CONFIGURATION_OPERATIONS.map((definition) => {
    const entry = entries.find(
      (candidate) => candidate.operationKey === definition.operationKey,
    );
    return {
      operationKey: definition.operationKey,
      catalogEntryId: entry?.catalogEntryId ?? null,
      resolvedCatalogEntry: entry?.resolvedCatalogEntry ?? null,
    };
  });

const buildSchoolPaymentMethodConfigurationCatalog = (
  paymentReadiness: PaymentControlPlaneReadinessResponse,
): SchoolPaymentMethodConfigurationCatalogEntry[] =>
  paymentReadiness.paymentMethodConfigurations.schoolCatalog.map((entry) => ({
    ...entry,
  }));

const buildOperationDefinitionsByLaneGroup = (): Array<{
  group: PaymentMethodConfigurationLaneGroup;
  definitions: PaymentMethodConfigurationOperationDefinition[];
}> => {
  const assignedOperationKeys = new Set<PaymentCheckoutOperationKey>();
  return PAYMENT_METHOD_CONFIGURATION_LANE_GROUPS.map((group) => {
    const definitions =
      group.id === "other"
        ? PAYMENT_METHOD_CONFIGURATION_OPERATIONS.filter(
            (definition) => !assignedOperationKeys.has(definition.operationKey),
          )
        : PAYMENT_METHOD_CONFIGURATION_OPERATIONS.filter((definition) => {
            const matches = group.match(definition.operationKey);
            if (matches) {
              assignedOperationKeys.add(definition.operationKey);
            }
            return matches;
          });
    return { group, definitions };
  });
};

const buildEffectivePaymentMethodConfigurationOptions = (
  paymentReadiness: PaymentControlPlaneReadinessResponse | undefined,
  schoolCatalog: SchoolPaymentMethodConfigurationCatalogEntry[],
): Array<{ value: string; label: string }> => {
  const platformCatalog =
    paymentReadiness?.paymentMethodConfigurations?.platformCatalog ?? [];
  const catalog = [
    ...platformCatalog,
    ...normalizeSchoolPaymentMethodConfigurationCatalog(schoolCatalog),
  ];
  const entries = catalog.map((entry) => ({
    value: entry.id,
    label: `${entry.label}${entry.source === "platform" ? " · platform" : " · school"}${entry.enabled ? "" : " · disabled"}${entry.paymentMethodConfigurationId ? "" : " · default Stripe behavior"}`,
  }));
  return [{ value: "", label: "Use current default (unassigned)" }, ...entries];
};

const formatIssueSeverity = (severity: PaymentIssueSeverity): string => {
  switch (severity) {
    case "warning":
      return "Warning";
    case "info":
      return "Info";
    default:
      return "Error";
  }
};

const issueSeverityClasses = (severity: PaymentIssueSeverity): string => {
  switch (severity) {
    case "warning":
      return "border-coral bg-warning-background text-irongrey";
    case "info":
      return "border-primary/20 bg-info-background text-primary";
    default:
      return "border-strawberry/25 bg-error-background text-strawberry";
  }
};

function PaymentPolicyStage({
  step,
  title,
  description,
}: {
  step: string;
  title: string;
  description: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-white px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#237062]">
        {step}
      </p>
      <p className="mt-2 text-sm font-semibold text-irongrey">{title}</p>
      <p className="mt-1 text-xs leading-5 text-text-secondary">
        {description}
      </p>
    </div>
  );
}

function DisclosureSection({
  title,
  description,
  isOpen,
  onToggle,
  testId,
  children,
}: {
  title: string;
  description?: string;
  isOpen: boolean;
  onToggle: () => void;
  testId: string;
  children: ReactNode;
}) {
  const panelId = `${testId}-panel`;
  return (
    <div className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB]">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={onToggle}
        data-testid={testId}
      >
        <span>
          <span className="block text-xs font-semibold uppercase tracking-[0.16em] text-[#237062]">
            {title}
          </span>
          {description ? (
            <span className="mt-1 block text-sm normal-case tracking-normal text-[#374151]">
              {description}
            </span>
          ) : null}
        </span>
        <span className="text-lg font-semibold text-primary" aria-hidden="true">
          {isOpen ? "-" : "+"}
        </span>
      </button>
      {isOpen ? (
        <div id={panelId} className="border-t border-[#E5E7EB] px-4 py-4">
          {children}
        </div>
      ) : null}
    </div>
  );
}

function UnsavedPaymentMethodConfigurationBanner({
  onReset,
}: {
  onReset: () => void;
}) {
  return (
    <div
      className="flex flex-col gap-3 rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-sm text-[#92400E] md:flex-row md:items-center md:justify-between"
      data-testid="settings-general-payments-unsaved-banner"
    >
      <span>
        Unsaved payment method option changes are staged for this school.
      </span>
      <button
        type="button"
        className="text-left text-xs font-semibold uppercase tracking-[0.12em] text-[#92400E] underline underline-offset-4 md:text-right"
        onClick={onReset}
      >
        Reset changes
      </button>
    </div>
  );
}

function IssueToneIcon({ severity }: { severity: PaymentIssueSeverity }) {
  if (severity === "warning") {
    return (
      <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-irongrey" />
    );
  }
  return <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-strawberry" />;
}

export function StripePaymentMethodConfigurationCard({
  canManageCatalog,
}: {
  canManageCatalog: boolean;
}) {
  const paymentReadinessQuery = useGetPaymentControlPlaneReadinessQuery();
  const [updatePaymentControlPlane, updatePaymentControlPlaneState] =
    useUpdatePaymentControlPlaneMutation();
  const paymentReadiness = paymentReadinessQuery.data;
  const paymentMethodConfigurationIssues =
    paymentReadiness?.paymentMethodConfigurations?.issues ?? [];
  const paymentIssues = paymentReadiness?.issues ?? [];
  const [
    schoolPaymentMethodConfigurationCatalog,
    setSchoolPaymentMethodConfigurationCatalog,
  ] = useState<SchoolPaymentMethodConfigurationCatalogEntry[]>([]);
  const [
    paymentMethodConfigurationAssignments,
    setPaymentMethodConfigurationAssignments,
  ] = useState<EditablePaymentMethodConfigurationAssignment[]>([]);
  const [paymentMethodConfigurationDraft, setPaymentMethodConfigurationDraft] =
    useState<PaymentMethodConfigurationDraftState>(
      emptySchoolPaymentMethodConfigurationDraft(),
    );
  const [
    editingPaymentMethodConfigurationId,
    setEditingPaymentMethodConfigurationId,
  ] = useState("");
  const [
    paymentMethodConfigurationMessage,
    setPaymentMethodConfigurationMessage,
  ] = useState("");
  const [paymentMethodConfigurationError, setPaymentMethodConfigurationError] =
    useState("");
  const [isHowThisWorksOpen, setHowThisWorksOpen] = useState(false);
  const [isCatalogReferenceOpen, setCatalogReferenceOpen] = useState(false);
  const [isCatalogAuthoringOpen, setCatalogAuthoringOpen] = useState(false);

  const paymentMethodConfigurationOptions = useMemo(
    () =>
      buildEffectivePaymentMethodConfigurationOptions(
        paymentReadiness,
        schoolPaymentMethodConfigurationCatalog,
      ),
    [
      paymentReadiness?.paymentMethodConfigurations,
      schoolPaymentMethodConfigurationCatalog,
    ],
  );

  const paymentMethodConfigurationDirty = useMemo(
    () =>
      serializePaymentMethodConfigurationState(
        schoolPaymentMethodConfigurationCatalog,
        paymentMethodConfigurationAssignments,
      ) !==
      serializePaymentMethodConfigurationState(
        paymentReadiness?.paymentMethodConfigurations?.schoolCatalog ?? [],
        (
          paymentReadiness?.paymentMethodConfigurations?.effectiveAssignments ??
          []
        ).map((entry) => ({
          operationKey: entry.operationKey,
          catalogEntryId: entry.catalogEntryId ?? null,
          resolvedCatalogEntry: entry.resolvedCatalogEntry ?? null,
        })),
      ),
    [
      paymentMethodConfigurationAssignments,
      paymentReadiness?.paymentMethodConfigurations?.effectiveAssignments,
      paymentReadiness?.paymentMethodConfigurations?.schoolCatalog,
      schoolPaymentMethodConfigurationCatalog,
    ],
  );

  const readinessPaymentMethodConfigurationAssignments = useMemo(
    () =>
      buildEditablePaymentMethodConfigurationAssignments(
        paymentReadiness?.paymentMethodConfigurations?.effectiveAssignments ??
          [],
      ),
    [paymentReadiness?.paymentMethodConfigurations?.effectiveAssignments],
  );

  const readinessPaymentMethodConfigurationAssignmentsByOperation = useMemo(
    () =>
      new Map(
        readinessPaymentMethodConfigurationAssignments.map(
          (entry) => [entry.operationKey, entry] as const,
        ),
      ),
    [readinessPaymentMethodConfigurationAssignments],
  );

  const currentPaymentMethodConfigurationAssignmentsByOperation = useMemo(
    () =>
      new Map(
        paymentMethodConfigurationAssignments.map(
          (entry) => [entry.operationKey, entry] as const,
        ),
      ),
    [paymentMethodConfigurationAssignments],
  );

  const operationDefinitionsByLaneGroup = useMemo(
    () => buildOperationDefinitionsByLaneGroup(),
    [],
  );

  const hasBlockingPaymentIssues = useMemo(
    () =>
      paymentIssues.some(
        (issue) =>
          issue.severity === "error" || (issue.severity as string) === "blocking",
      ),
    [paymentIssues],
  );

  useEffect(() => {
    if (!paymentReadiness) {
      return;
    }
    setSchoolPaymentMethodConfigurationCatalog(
      buildSchoolPaymentMethodConfigurationCatalog(paymentReadiness),
    );
    setPaymentMethodConfigurationAssignments(
      buildEditablePaymentMethodConfigurationAssignments(
        paymentReadiness.paymentMethodConfigurations.effectiveAssignments,
      ),
    );
  }, [paymentReadiness?.paymentMethodConfigurations]);

  const resetPaymentMethodConfigurationChanges = () => {
    if (!paymentReadiness) {
      return;
    }
    setSchoolPaymentMethodConfigurationCatalog(
      buildSchoolPaymentMethodConfigurationCatalog(paymentReadiness),
    );
    setPaymentMethodConfigurationAssignments(
      buildEditablePaymentMethodConfigurationAssignments(
        paymentReadiness.paymentMethodConfigurations.effectiveAssignments,
      ),
    );
    setPaymentMethodConfigurationMessage("");
    setPaymentMethodConfigurationError("");
  };

  const isPaymentMethodConfigurationAssignmentDirty = (
    operationKey: PaymentCheckoutOperationKey,
  ): boolean => {
    const current =
      currentPaymentMethodConfigurationAssignmentsByOperation.get(operationKey)
        ?.catalogEntryId ?? null;
    const saved =
      readinessPaymentMethodConfigurationAssignmentsByOperation.get(operationKey)
        ?.catalogEntryId ?? null;
    return current !== saved;
  };

  const resetPaymentMethodConfigurationDraft = () => {
    setEditingPaymentMethodConfigurationId("");
    setPaymentMethodConfigurationDraft(
      emptySchoolPaymentMethodConfigurationDraft(),
    );
  };

  const handleUpsertSchoolPaymentMethodConfiguration = () => {
    const label = readText(paymentMethodConfigurationDraft.label);
    const paymentMethodConfigurationId = readText(
      paymentMethodConfigurationDraft.paymentMethodConfigurationId,
    );
    if (!label) {
      setPaymentMethodConfigurationMessage("");
      setPaymentMethodConfigurationError("Catalog label is required.");
      return;
    }
    if (!paymentMethodConfigurationId.startsWith("pmc_")) {
      setPaymentMethodConfigurationMessage("");
      setPaymentMethodConfigurationError(
        "Stripe configuration ID must start with pmc_.",
      );
      return;
    }

    const reservedIds = new Set(
      (
        paymentReadiness?.paymentMethodConfigurations?.platformCatalog ?? []
      ).map((entry) => entry.id),
    );
    const existingIds = new Set(
      schoolPaymentMethodConfigurationCatalog
        .filter((entry) => entry.id !== editingPaymentMethodConfigurationId)
        .map((entry) => entry.id),
    );

    let nextId =
      readText(editingPaymentMethodConfigurationId) ||
      slugifyPaymentMethodConfigurationId(label);
    let suffix = 2;
    while (reservedIds.has(nextId) || existingIds.has(nextId)) {
      nextId = `${slugifyPaymentMethodConfigurationId(label)}_${suffix}`;
      suffix += 1;
    }

    const nextEntry: SchoolPaymentMethodConfigurationCatalogEntry = {
      id: nextId,
      label,
      paymentMethodConfigurationId,
      enabled: Boolean(paymentMethodConfigurationDraft.enabled),
      source: "school",
    };

    setSchoolPaymentMethodConfigurationCatalog((current) => {
      const withoutEdited = current.filter(
        (entry) => entry.id !== editingPaymentMethodConfigurationId,
      );
      return normalizeSchoolPaymentMethodConfigurationCatalog([
        ...withoutEdited,
        nextEntry,
      ]);
    });
    setPaymentMethodConfigurationError("");
    setPaymentMethodConfigurationMessage(
      editingPaymentMethodConfigurationId
        ? `Updated school payment method option ${label}.`
        : `Added school payment method option ${label}.`,
    );
    resetPaymentMethodConfigurationDraft();
  };

  const handleEditSchoolPaymentMethodConfiguration = (
    entry: SchoolPaymentMethodConfigurationCatalogEntry,
  ) => {
    setEditingPaymentMethodConfigurationId(entry.id);
    setCatalogAuthoringOpen(true);
    setPaymentMethodConfigurationDraft({
      id: entry.id,
      label: entry.label,
      paymentMethodConfigurationId: entry.paymentMethodConfigurationId,
      enabled: entry.enabled,
    });
    setPaymentMethodConfigurationError("");
    setPaymentMethodConfigurationMessage("");
  };

  const handleDeleteSchoolPaymentMethodConfiguration = (
    catalogEntryId: string,
  ) => {
    setSchoolPaymentMethodConfigurationCatalog((current) =>
      current.filter((entry) => entry.id !== catalogEntryId),
    );
    setPaymentMethodConfigurationAssignments((current) =>
      current.map((entry) =>
        entry.catalogEntryId === catalogEntryId
          ? {
              operationKey: entry.operationKey,
              catalogEntryId: null,
              resolvedCatalogEntry: null,
            }
          : entry,
      ),
    );
    if (editingPaymentMethodConfigurationId === catalogEntryId) {
      resetPaymentMethodConfigurationDraft();
    }
    setPaymentMethodConfigurationError("");
    setPaymentMethodConfigurationMessage(
      `Removed school payment method option ${catalogEntryId}.`,
    );
  };

  const handlePaymentMethodConfigurationAssignmentChange = (
    operationKey: PaymentCheckoutOperationKey,
    catalogEntryId: string,
  ) => {
    const normalizedCatalogEntryId = readText(catalogEntryId) || null;
    const resolvedCatalogEntry = normalizedCatalogEntryId
      ? ((
          paymentReadiness?.paymentMethodConfigurations?.effectiveCatalog ?? []
        ).find((entry) => entry.id === normalizedCatalogEntryId) ??
        schoolPaymentMethodConfigurationCatalog.find(
          (entry) => entry.id === normalizedCatalogEntryId,
        ) ??
        null)
      : null;

    setPaymentMethodConfigurationAssignments((current) => {
      const nextAssignments = new Map(
        current.map((entry) => [entry.operationKey, entry] as const),
      );
      nextAssignments.set(operationKey, {
        operationKey,
        catalogEntryId: normalizedCatalogEntryId,
        resolvedCatalogEntry,
      });
      return PAYMENT_METHOD_CONFIGURATION_OPERATIONS.map(
        (definition) =>
          nextAssignments.get(definition.operationKey) ?? {
            operationKey: definition.operationKey,
            catalogEntryId: null,
            resolvedCatalogEntry: null,
          },
      );
    });
    setPaymentMethodConfigurationError("");
    setPaymentMethodConfigurationMessage("");
  };

  const handleSavePaymentMethodConfigurations = async () => {
    try {
      await updatePaymentControlPlane({
        schoolPaymentMethodConfigurationCatalog:
          normalizeSchoolPaymentMethodConfigurationCatalog(
            schoolPaymentMethodConfigurationCatalog,
          ),
        paymentMethodConfigurationAssignments:
          normalizePaymentMethodConfigurationAssignments(
            paymentMethodConfigurationAssignments,
          ),
      }).unwrap();
      setPaymentMethodConfigurationError("");
      setPaymentMethodConfigurationMessage(
        "Hosted checkout payment method assignments saved.",
      );
      await paymentReadinessQuery.refetch();
    } catch (error) {
      setPaymentMethodConfigurationMessage("");
      setPaymentMethodConfigurationError(
        `Hosted checkout payment method changes failed: ${readText((error as { message?: unknown })?.message, "Unknown error")}`,
      );
    }
  };

  const platformCatalog =
    paymentReadiness?.paymentMethodConfigurations?.platformCatalog ?? [];
  const paymentPolicyStages = canManageCatalog
    ? [
        {
          step: "1",
          title: "Platform defaults",
          description:
            "Read-only payment method options shared across schools, including the default fallback.",
        },
        {
          step: "2",
          title: "School options",
          description:
            "Optional school-local entries for tenant-specific Stripe payment-method policies.",
        },
        {
          step: "3",
          title: "Checkout assignments",
          description:
            "Each live hosted checkout operation chooses one catalog entry or stays unassigned.",
        },
        {
          step: "4",
          title: "Hosted Checkout",
          description:
            "Only the mapped checkout lane changes. Unassigned lanes keep the current Stripe default.",
        },
      ]
    : [
        {
          step: "1",
          title: "Catalog Authority",
          description:
            "Super-administrators manage the shared platform catalog and school-local payment method options.",
        },
        {
          step: "2",
          title: "Administrator Scope",
          description:
            "Administrators can keep checkout policy current by assigning the existing catalog in checkout assignments.",
        },
        {
          step: "3",
          title: "Checkout assignments",
          description:
            "Each live hosted checkout operation chooses one catalog entry or stays unassigned.",
        },
        {
          step: "4",
          title: "Hosted Checkout",
          description:
            "Only the mapped checkout lane changes. Unassigned lanes keep the current Stripe default.",
        },
      ];

  const renderPaymentReadinessIssues = () => (
    <div className="rounded-lg border border-[#E5E7EB] bg-white px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#237062]">
          Payments readiness issues
        </p>
        <span
          className="text-xs font-semibold text-text-secondary"
          data-testid="settings-general-payments-issues-count"
        >
          {paymentIssues.length}
        </span>
      </div>
      {paymentIssues.length ? (
        <ul
          className="mt-3 space-y-2"
          data-testid="settings-general-payments-issues-list"
        >
          {paymentIssues.map((issue) => (
            <li
              key={`${issue.code}:${issue.message}`}
              className={`rounded-md border px-3 py-3 ${issueSeverityClasses(issue.severity)}`}
            >
              <div className="flex items-start gap-3">
                <IssueToneIcon severity={issue.severity} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-semibold">
                      {formatIssueSeverity(issue.severity)}
                    </p>
                    <span className="text-[11px] uppercase tracking-[0.12em]">
                      {issue.code}
                    </span>
                  </div>
                  <p className="mt-1 text-sm">{issue.message}</p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p
          className="mt-3 rounded-md border border-dashed border-border bg-muted px-3 py-4 text-sm text-text-secondary"
          data-testid="settings-general-payments-issues-empty"
        >
          All payments readiness checks passed. No blockers or warnings are
          currently reported.
        </p>
      )}
    </div>
  );

  return (
    <Card data-testid="settings-general-payments-pmc-card">
      <SectionHeader
        title="Hosted checkout payment methods"
        description="Choose which payment methods appear in each hosted checkout. Leaving a checkout unassigned keeps the school default."
      />

      {!canManageCatalog ? (
        <div
          className="mb-4 rounded-lg border border-primary/20 bg-info-background px-4 py-4 text-sm text-irongrey"
          data-testid="settings-general-payments-catalog-restricted-note"
        >
          Catalog management is limited to super-administrators.
          Administrators can still assign the existing payment method options
          to live checkout flows.
        </div>
      ) : null}

      <DisclosureSection
        title="How this works"
        description="See how catalog options become checkout assignments."
        isOpen={isHowThisWorksOpen}
        onToggle={() => setHowThisWorksOpen((current) => !current)}
        testId="settings-general-payments-how-it-works-toggle"
      >
        <p className="text-sm text-[#374151]">
          The policy model is explicit: platform defaults stay read-only,
          schools can add local payment method options, and checkout
          assignments decide which hosted checkout flows opt into a different
          Stripe payment-method policy.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-3 xl:grid-cols-4">
          {paymentPolicyStages.map((stage) => (
            <PaymentPolicyStage
              key={stage.step}
              step={stage.step}
              title={stage.title}
              description={stage.description}
            />
          ))}
        </div>
      </DisclosureSection>

      {paymentReadinessQuery.isLoading && !paymentReadiness ? (
        <div
          className="rounded-lg border border-border bg-white px-4 py-4 text-sm text-text-secondary"
          data-testid="settings-general-payments-loading"
        >
          Loading Stripe payment method policy…
        </div>
      ) : null}

      {paymentReadinessQuery.error && !paymentReadiness ? (
        <SupportErrorMessage
          className="rounded-lg px-4 py-4"
          data-testid="settings-general-payments-load-error"
        >
          Unable to load Stripe payment method policy.
        </SupportErrorMessage>
      ) : null}

      {paymentReadiness ? (
        <>
          {paymentMethodConfigurationDirty ? (
            <UnsavedPaymentMethodConfigurationBanner
              onReset={resetPaymentMethodConfigurationChanges}
            />
          ) : null}

          {hasBlockingPaymentIssues ? renderPaymentReadinessIssues() : null}

          <div className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-4">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#237062]">
              Checkout assignments
            </p>
            <p className="mt-2 text-sm text-[#374151]">
              Only the selected hosted checkout operation changes. Remove the
              assignment to fall back to the school&apos;s current Stripe
              default.
            </p>
            <div className="mt-3 space-y-4">
              {operationDefinitionsByLaneGroup.map(({ group, definitions }) => (
                <div key={group.id} className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-irongrey/60">
                    {group.label}
                  </p>
                  {definitions.length > 0 ? (
                    definitions.map((definition) => {
                      const currentAssignment =
                        currentPaymentMethodConfigurationAssignmentsByOperation.get(
                          definition.operationKey,
                        ) ?? {
                          operationKey: definition.operationKey,
                          catalogEntryId: null,
                          resolvedCatalogEntry: null,
                        };
                      const selectedCatalogId =
                        currentAssignment.catalogEntryId ?? "";
                      const rowDirty =
                        isPaymentMethodConfigurationAssignmentDirty(
                          definition.operationKey,
                        );
                      return (
                        <div
                          key={definition.operationKey}
                          className="grid grid-cols-1 gap-3 rounded-md border border-border bg-white px-3 py-3 md:grid-cols-[minmax(0,1fr)_320px]"
                        >
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm font-semibold text-irongrey">
                                {definition.label}
                              </p>
                              {rowDirty ? (
                                <span className="rounded-full bg-[#FEF3C7] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#92400E]">
                                  Changed
                                </span>
                              ) : null}
                            </div>
                            <p className="mt-1 text-xs text-text-secondary">
                              {definition.description}
                            </p>
                            <p className="mt-2 text-[11px] uppercase tracking-[0.12em] text-text-secondary">
                              {definition.operationKey}
                            </p>
                          </div>
                          <div>
                            <label className="block">
                              <span className="mb-1 block text-sm font-medium text-irongrey">
                                Assigned payment method
                              </span>
                              <select
                                value={selectedCatalogId}
                                onChange={(event) =>
                                  handlePaymentMethodConfigurationAssignmentChange(
                                    definition.operationKey,
                                    event.currentTarget.value,
                                  )
                                }
                                className="block h-10 w-full rounded-md border border-border bg-white px-3 text-sm text-irongrey shadow-sm focus:border-primary focus:ring-primary"
                                data-testid={`settings-general-payments-operation-${definition.operationKey}`}
                              >
                                {paymentMethodConfigurationOptions.map(
                                  (option) => (
                                    <option
                                      key={option.value || "unassigned"}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </option>
                                  ),
                                )}
                              </select>
                            </label>
                            <button
                              type="button"
                              className="mt-2 text-xs font-semibold text-primary underline underline-offset-4 disabled:text-text-secondary disabled:no-underline"
                              disabled={!selectedCatalogId}
                              onClick={() =>
                                handlePaymentMethodConfigurationAssignmentChange(
                                  definition.operationKey,
                                  "",
                                )
                              }
                            >
                              Reset to default
                            </button>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-irongrey/60">
                      No checkout assignments in this group.
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {paymentMethodConfigurationMessage ? (
            <p className="rounded-md border border-[#BBF7D0] bg-[#F0FDF4] px-3 py-2 text-sm text-[#166534]">
              {paymentMethodConfigurationMessage}
            </p>
          ) : null}
          {paymentMethodConfigurationError ? (
            <p className="rounded-md border border-[#FECACA] bg-[#FEF2F2] px-3 py-2 text-sm text-[#991B1B]">
              {paymentMethodConfigurationError}
            </p>
          ) : null}

          {paymentMethodConfigurationIssues.length > 0 ? (
            <div className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-sm text-[#92400E]">
              Payment method option readiness issues are also included in the
              global payments readiness list. Resolve duplicate ids, missing
              catalog entries, or disabled assignments before relying on a
              mapped checkout lane.
            </div>
          ) : null}

          <div className="flex items-center justify-between gap-3 my-4">
            <p className="text-xs text-text-secondary">
              {canManageCatalog
                ? "Saving writes the school-local catalog and checkout assignments into the tenant Stripe payments control plane."
                : "Saving writes checkout assignments into the tenant Stripe payments control plane."}
            </p>
            <Button
              type="button"
              variant="primary"
              disabled={
                updatePaymentControlPlaneState.isLoading ||
                !paymentMethodConfigurationDirty
              }
              onClick={() => void handleSavePaymentMethodConfigurations()}
              data-testid="settings-general-payments-pmc-matrix-save-button"
            >
              {updatePaymentControlPlaneState.isLoading
                ? "Saving..."
                : "Save changes"}
            </Button>
          </div>

          {!hasBlockingPaymentIssues ? renderPaymentReadinessIssues() : null}

          <DisclosureSection
            title="Available payment method options"
            description="Review the platform defaults and school-added options."
            isOpen={isCatalogReferenceOpen}
            onToggle={() => setCatalogReferenceOpen((current) => !current)}
            testId="settings-general-payments-catalog-reference-toggle"
          >
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              <div
                className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3"
                data-testid="settings-general-payments-platform-catalog"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#237062]">
                  Platform catalog
                </p>
                <div className="mt-3 space-y-2">
                  {platformCatalog.map((entry) => (
                    <div
                      key={entry.id}
                      className="rounded-md border border-border bg-white px-3 py-3"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-sm font-semibold text-irongrey">
                              {entry.label}
                            </p>
                            <span className="rounded-full bg-muted px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-text-secondary">
                              Platform default
                            </span>
                          </div>
                          <p className="mt-1 text-xs text-text-secondary">
                            {entry.paymentMethodConfigurationId ??
                              "Default Stripe behavior"}
                          </p>
                        </div>
                        <span className="rounded-full bg-muted px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-text-secondary">
                          {entry.enabled ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div
                className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3"
                data-testid="settings-general-payments-school-catalog"
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#237062]">
                    School catalog
                  </p>
                  <span
                    className="text-xs font-semibold text-text-secondary"
                    data-testid="settings-general-payments-school-pmc-count"
                  >
                    {schoolPaymentMethodConfigurationCatalog.length}
                  </span>
                </div>
                <div className="mt-3 space-y-2">
                  {schoolPaymentMethodConfigurationCatalog.length > 0 ? (
                    schoolPaymentMethodConfigurationCatalog.map((entry) => (
                      <div
                        key={entry.id}
                        className="rounded-md border border-border bg-white px-3 py-3"
                        data-testid={`settings-general-payments-school-pmc-${entry.id}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm font-semibold text-irongrey">
                                {entry.label}
                              </p>
                              <span className="rounded-full bg-muted px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-text-secondary">
                                School-added
                              </span>
                            </div>
                            <p className="mt-1 text-xs text-text-secondary">
                              {entry.paymentMethodConfigurationId}
                            </p>
                            <p className="mt-1 text-[11px] uppercase tracking-[0.12em] text-text-secondary">
                              {entry.enabled ? "Enabled" : "Disabled"} ·{" "}
                              {entry.id}
                            </p>
                          </div>
                          {canManageCatalog ? (
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                className="px-3 py-1.5 text-[10px]"
                                onClick={() =>
                                  handleEditSchoolPaymentMethodConfiguration(
                                    entry,
                                  )
                                }
                              >
                                Edit
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                className="px-3 py-1.5 text-[10px] text-strawberry hover:text-strawberry"
                                onClick={() =>
                                  handleDeleteSchoolPaymentMethodConfiguration(
                                    entry.id,
                                  )
                                }
                              >
                                Delete
                              </Button>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="rounded-md border border-dashed border-border bg-white px-3 py-4 text-sm text-text-secondary">
                      No school-local payment method options yet. Start from
                      the platform defaults, then add school-specific options
                      only when a hosted checkout lane needs its own Stripe
                      policy.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </DisclosureSection>

          {canManageCatalog ? (
            <DisclosureSection
              title={
                editingPaymentMethodConfigurationId
                  ? "Edit payment method option"
                  : "Add payment method option"
              }
              description="Manage school-local Stripe options for hosted checkout assignments."
              isOpen={isCatalogAuthoringOpen}
              onToggle={() => setCatalogAuthoringOpen((current) => !current)}
              testId="settings-general-payments-catalog-authoring-toggle"
            >
              <div
                className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-4"
                data-testid="settings-general-payments-catalog-management"
              >
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <Input
                    label="Label"
                    value={paymentMethodConfigurationDraft.label}
                    onChange={(event) =>
                      setPaymentMethodConfigurationDraft((current) => ({
                        ...current,
                        label: event.currentTarget.value,
                      }))
                    }
                    placeholder="ACH Only App"
                  />
                  <Input
                    label="Stripe configuration ID"
                    value={
                      paymentMethodConfigurationDraft.paymentMethodConfigurationId
                    }
                    onChange={(event) =>
                      setPaymentMethodConfigurationDraft((current) => ({
                        ...current,
                        paymentMethodConfigurationId: event.currentTarget.value,
                      }))
                    }
                    placeholder="pmc_..."
                  />
                </div>
                <label className="mt-3 inline-flex items-center gap-2 text-sm text-irongrey">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                    checked={paymentMethodConfigurationDraft.enabled}
                    onChange={(event) =>
                      setPaymentMethodConfigurationDraft((current) => ({
                        ...current,
                        enabled: event.currentTarget.checked,
                      }))
                    }
                  />
                  Enabled for assignment
                </label>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="primary"
                    onClick={handleUpsertSchoolPaymentMethodConfiguration}
                    data-testid="settings-general-payments-school-pmc-save-button"
                  >
                    {editingPaymentMethodConfigurationId
                      ? "Update option"
                      : "Add option"}
                  </Button>
                  {editingPaymentMethodConfigurationId ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={resetPaymentMethodConfigurationDraft}
                    >
                      Cancel edit
                    </Button>
                  ) : null}
                </div>
              </div>
            </DisclosureSection>
          ) : null}
        </>
      ) : null}
    </Card>
  );
}
